// Read-only signal chips and empty-section evidence for the Updates reader.
// Derived from the same selected-week project payload the sections render;
// unknown evidence stays "unknown" (never zero) and no money is exposed.
import { assignedTrackCompleteness } from "../../../lib/weeklyTrackCompleteness.js";
import { weeklyText } from "../../../lib/weeklyRichText.js";
import { budgetHealth } from "../../../lib/projectDisplaySignals.js";

const isOpen = (row) => !row?.status || row.status === "open";
export const FINANCIAL_MEASURES = ["budget", "spendToDate", "forecast", "variance"];
export const hasFinancialValue = (value) => value != null && String(value).trim() !== "" && Number.isFinite(Number(value));

export function openDecisions(project) {
  return Array.isArray(project?.decisions) ? project.decisions.filter(isOpen) : null;
}
export function openRisks(project) {
  return Array.isArray(project?.risks) ? project.risks.filter(isOpen) : null;
}

// Qualitative only: one budget word, or why it is unknown. Colour is reserved
// for over budget (PROJECTS-CALM-PASS R2): amber up to 5% over, red above.
export function budgetWord(financials) {
  if (!financials) return { value: "Unavailable", tone: "unknown" };
  const health = budgetHealth(financials.budget, financials.forecast);
  if (!health || health.state === "not_tracked" || !Number.isFinite(health.ratio)) return { value: "Not tracked", tone: "unknown" };
  if (health.state === "over") return { value: "Over", tone: "danger" };
  if (health.state === "amber") return { value: "Over", tone: "warn" };
  return { value: health.ratio === 1 ? "On budget" : "Under", tone: "neutral" };
}
export const varianceSignal = budgetWord;

// Four neutral stats for the selected project (PROJECTS-CALM-PASS 3c).
// Next milestone moved into the header; unknown evidence is never zero.
export function readerSignals(project) {
  const tracks = assignedTrackCompleteness(project);
  const decisions = openDecisions(project);
  const risks = openRisks(project);
  return [
    { key: "tracks", label: "Tracks posted", value: tracks.available ? `${tracks.inCount} of ${tracks.total}` : "Unknown", tone: tracks.available ? "neutral" : "unknown",
      detail: tracks.available ? (tracks.quietCount ? `${tracks.quietCount} quiet` : null) : "assignments not captured" },
    { key: "decisions", label: "Decisions open", value: decisions ? String(decisions.length) : "Unknown", tone: decisions ? "neutral" : "unknown", detail: null },
    { key: "risks", label: "Risks open", value: risks ? String(risks.length) : "Unknown", tone: risks ? "neutral" : "unknown", detail: null },
    { key: "budget", label: "Budget", ...budgetWord(project?.financials) },
  ];
}

// Genuinely empty = evidence is present and contains nothing. Missing archive
// evidence, restricted or failed data are NOT empty and never collapse.
export function sectionEmptiness(project) {
  const s = project?.summary || {};
  const captured = Object.hasOwn(project || {}, "summary");
  const decisions = openDecisions(project);
  const risks = openRisks(project);
  return {
    thisWeek: captured && !weeklyText(s.accomplishments).trim() && !weeklyText(s.upcoming).trim() && !weeklyText(s.blockers).trim(),
    decisions: Array.isArray(decisions) && decisions.length === 0,
    risks: Array.isArray(risks) && risks.length === 0,
    financials: Boolean(project?.financials) && !FINANCIAL_MEASURES.some(key => hasFinancialValue(project.financials[key])),
    pmo: captured && !weeklyText(s.pmoNote).trim(),
  };
}

export const EMPTY_BAR_TEXT = {
  thisWeek: "This week · not posted",
  decisions: "Decisions · 0 — none open",
  risks: "Risks · 0 — none this week",
  pmo: "PMO note · not posted",
};

export const EMPTY_SECTION_MESSAGE = {
  thisWeek: "not posted",
  financials: "not recorded",
  decisions: "none",
  risks: "none",
};
